import React, { useCallback, useEffect, useRef, useState } from "react";
import { Calendar as CalIcon, Clock, MapPin, User, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useBooking } from "@/context/BookingContext";
import { StepTitle, PrimaryButton } from "@/components/BookingShell";
import { formatPrice, formatDuration, formatFullDate, computeDeposit } from "@/lib/format";
import { GoldRule } from "@/components/Brand";

function Row({ icon: Icon, label, value, testId }) {
    return (
        <div className="flex items-start gap-4" data-testid={testId}>
            <div className="mt-0.5 h-9 w-9 rounded-full glass flex items-center justify-center">
                <Icon className="h-4 w-4 text-gold" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">{label}</div>
                <div className="mt-0.5 text-white font-ui text-base leading-snug">{value}</div>
            </div>
        </div>
    );
}

export function ReviewStep({ setFooter }) {
    const { service, date, time, customer, goTo, setConfirmation, cart } = useBooking();
    const { hasDeposit, depositDue } = computeDeposit(service);

    // Two-phase submitting state so the button reflects the true stage.
    const [phase, setPhase] = useState("idle"); // "idle" | "creating" | "redirecting"
    const [recoveryAvailable, setRecoveryAvailable] = useState(() => { try { return !!sessionStorage.getItem("pout_cart_request"); } catch { return false; } });
    const submittingRef = useRef(false);
    const busy = phase !== "idle";

    const submit = useCallback(async () => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        setPhase("creating");
        try {
            if (service?.cartQuote) {
                const payload = {items:cart.map((c) => ({service_id:c.service.id,quantity:c.quantity})), date, time,
                    customer, expected_price:service.cartQuote.price, expected_due:service.cartQuote.amount_due};
                const previous = JSON.parse(sessionStorage.getItem("pout_cart_request") || "null");
                if (previous && previous.payload !== JSON.stringify(payload)) {
                    throw new Error("You already have a checkout in progress. Return to it before starting another booking.");
                }
                const key = previous?.key || Array.from(crypto.getRandomValues(new Uint8Array(32)), (b) => b.toString(16).padStart(2,"0")).join("");
                sessionStorage.setItem("pout_cart_request", JSON.stringify({key, payload:JSON.stringify(payload)}));
                sessionStorage.setItem("pout_cart_key", key);
                setRecoveryAvailable(true);
                await api.cartCheckout(payload, key);
                window.location.assign("/booking/cart-payment");
                return;
            }
            const res = await api.createAppointment({
                service_id: service.id,
                date,
                time,
                customer: {
                    firstName: customer.firstName.trim(),
                    lastName: customer.lastName.trim(),
                    email: customer.email.trim(),
                    phone: customer.phone.trim(),
                    notes: customer.notes?.trim() || null,
                },
            });
            setConfirmation(res);

            // Record marketing opt-in choices (fire-and-forget). Both checkboxes
            // default to false; if the customer left them off, no consent is
            // recorded. Opt-outs are always honoured on the server side.
            try {
                if (res.customer_id && (customer.marketingEmail || customer.marketingSms)) {
                    api.postPublicMarketingOptIn?.({
                        customer_id: res.customer_id,
                        email: customer.email?.trim() || undefined,
                        phone: customer.phone?.trim() || undefined,
                        marketing_email: !!customer.marketingEmail,
                        marketing_sms: !!customer.marketingSms,
                        marketing_whatsapp: !!customer.marketingSms,
                        source: "booking_form",
                    })?.catch(() => {});
                }
            } catch { /* never break a booking on consent recording */ }

            // Persist minimal recovery details so returning from Stripe still
            // finds the booking on refresh.
            try {
                sessionStorage.setItem("pout_last_booking", JSON.stringify({
                    appointment_id: res.appointment_id,
                    booking_token: res.booking_token,
                    customer_first_name: customer.firstName,
                    service_id: service.id,
                }));
            } catch { /* private mode */ }

            if (res.requires_payment && res.payment_link_url) {
                setPhase("redirecting");
                // Give the UI a beat to flip its label so mobile users see the state change,
                // then redirect in-place (same tab — no popup blockers on mobile).
                window.setTimeout(() => {
                    window.location.assign(res.payment_link_url);
                }, 350);
                return;
            }
            if (res.requires_payment && !res.payment_link_url) {
                // Booking created but payment-link generation failed. Send to the
                // recovery page so the customer can retry — do NOT create another booking.
                submittingRef.current = false;
                setPhase("idle");
                const t = encodeURIComponent(res.booking_token || "");
                window.location.assign(
                    `/booking/payment-pending?appointment_id=${res.appointment_id}&token=${t}&err=1`
                );
                return;
            }
            // No deposit required → straight to the branded success screen.
            submittingRef.current = false;
            setPhase("idle");
            goTo("success");
        } catch (err) {
            if (service?.cartQuote && [409, 422, 503].includes(err.status)) {
                // Only release a rejected draft after the server verifies that
                // no durable order was created. Never clear an ambiguous write.
                try { await api.cartSession(sessionStorage.getItem("pout_cart_key")); }
                catch (check) { if (check.status === 404) { sessionStorage.removeItem("pout_cart_request"); sessionStorage.removeItem("pout_cart_key"); setRecoveryAvailable(false); } }
            }
            toast.error(err?.message || "Could not confirm booking. Please try again.");
            submittingRef.current = false;
            setPhase("idle");
        }
    }, [service, date, time, customer, goTo, setConfirmation, cart]);

    const buttonLabel = () => {
        if (phase === "creating")     return "Creating your booking…";
        if (phase === "redirecting")  return "Opening secure payment…";
        return hasDeposit ? "Continue to secure payment" : "Confirm booking";
    };

    useEffect(() => {
        setFooter(
            <PrimaryButton onClick={submit} disabled={busy} testId="review-confirm-button">
                {hasDeposit && phase === "idle" && <Lock className="inline h-3.5 w-3.5 mr-2 -mt-0.5" strokeWidth={2} />}
                {buttonLabel()}
            </PrimaryButton>
        );
        return () => setFooter(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase, busy, submit, setFooter, hasDeposit]);

    return (
        <div>
            <StepTitle
                eyebrow="Review"
                title={<>Confirm your <em className="italic gold-text not-italic font-serif">appointment</em></>}
                subtitle="Please take a moment to review the details."
                testId="review-step-title"
            />

            <div className="rounded-3xl glass p-6 space-y-5 animate-fade-up" data-testid="review-card">
                <Row icon={CalIcon} label="Date" value={formatFullDate(date)} testId="review-date" />
                <Row icon={Clock} label="Time" value={`${time} — ${formatDuration(service.duration)}`} testId="review-time" />
                <Row icon={MapPin} label="Treatment" value={service.name} testId="review-service" />
                <Row icon={User} label="Practitioner" value="Angela" testId="review-practitioner" />

                <GoldRule className="py-1" />

                <div className="space-y-1">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">Booked for</div>
                    <div className="text-white font-ui" data-testid="review-customer-name">
                        {customer.firstName} {customer.lastName}
                    </div>
                    <div className="text-sm text-white/60" data-testid="review-customer-contact">
                        {customer.email} · {customer.phone}
                    </div>
                    {customer.notes && (
                        <div className="mt-3 text-sm text-white/70 italic" data-testid="review-notes">
                            &ldquo;{customer.notes}&rdquo;
                        </div>
                    )}
                </div>

                <GoldRule className="py-1" />

                {(() => {
                    const { depositDue, remaining, hasDeposit } = computeDeposit(service);
                    if (!hasDeposit) {
                        return (
                            <div className="flex items-baseline justify-between" data-testid="review-price-block">
                                <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">Treatment price</div>
                                <div className="font-serif text-3xl gold-text" data-testid="review-total">
                                    {formatPrice(service.price)}
                                </div>
                            </div>
                        );
                    }
                    return (
                        <div className="space-y-3" data-testid="review-price-block">
                            <div className="flex items-baseline justify-between">
                                <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">Treatment price</div>
                                <div className="font-ui text-base text-white/85" data-testid="review-full-price">
                                    {formatPrice(service.price)}
                                </div>
                            </div>
                            <div className="flex items-baseline justify-between">
                                <div className="text-[10px] uppercase tracking-[0.24em] text-gold/85">{service?.cartQuote ? "Due today" : "Deposit due now"}</div>
                                <div className="font-serif text-3xl gold-text" data-testid="review-deposit-due">
                                    {formatPrice(depositDue)}
                                </div>
                            </div>
                            <div className="flex items-baseline justify-between">
                                <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">Balance at your appointment</div>
                                <div className="font-ui text-base text-white/70" data-testid="review-remaining">
                                    {remaining > 0 ? formatPrice(remaining) : "£0"}
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </div>

            <p className="mt-4 text-xs text-white/50 text-center max-w-md mx-auto leading-relaxed" data-testid="review-payment-notice">
                {hasDeposit ? (
                    <>
                        <Lock className="inline h-3 w-3 mr-1 -mt-0.5 text-gold/70" strokeWidth={2} />
                        After confirming, you'll be taken securely to Stripe to pay your{" "}
                        <span className="text-gold">{formatPrice(depositDue)}</span>{service?.cartQuote ? " due today" : " deposit"}. Your appointment will be confirmed once payment is completed.
                    </>
                ) : (
                    <>No payment is required today. Confirm your booking to complete your appointment request.</>
                )}
            </p>
            {service?.cartQuote && recoveryAvailable && <a className="flex min-h-11 items-center justify-center mt-3 text-gold underline text-sm" href="/booking/cart-payment">Return to your existing checkout</a>}
        </div>
    );
}
